import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { fetchAuthSession } from 'aws-amplify/auth';

import { environment } from '../config/environment';
import { parseTenantSlugFromPath } from '@cnapse/shared/utils/app-url.js';
import { TenantResponseSchema } from '../../../shared/src/tenant.schema';
import * as dt from '@cnapse/shared/data.types.js';

/**
 * Tenant context data
 */
export interface TenantContext {
  tenantId: string | null;
  tenantName: string | null;
  subdomain: string | null;
  isActive: boolean;
  hasSubscription: boolean;
  subscriptionStatus: string | null;
  accessReason: string | null;
  billingMode: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Context value with helper methods
 */
interface SubdomainContextValue extends TenantContext {
  refreshTenant: () => Promise<void>;
  clearTenant: () => void;
}

const SubdomainContext = createContext<SubdomainContextValue | undefined>(undefined);

/**
 * Extract tenant slug from the first URL path segment (e.g. /acme/dashboard → acme).
 */
function extractTenantSlug(pathname: string): string | null {
  return parseTenantSlugFromPath(pathname);
}

async function resolveBearerAccessToken(): Promise<string | null> {
  const cached = localStorage.getItem('wafer_access_token');
  if (cached) {
    return cached;
  }

  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch (error) {
    console.warn(
      'Failed to read Amplify session for tenant fetch, using cached token:',
      error instanceof Error ? error.message : error,
    );
  }
  return null;
}

/**
 * SubdomainProvider component
 * Resolves tenant slug from URL path and provides tenant subscription context.
 */
export function SubdomainProvider({
  children,
  userRole
}: {
  children: ReactNode;
  userRole?: string;
}): React.JSX.Element {

  const [context, setContext] = useState<TenantContext>(() => {
    const initialSlug =
      typeof window !== 'undefined' ? extractTenantSlug(window.location.pathname) : null;
    return {
      tenantId: null,
      tenantName: null,
      subdomain: initialSlug,
      isActive: false,
      hasSubscription: false,
      subscriptionStatus: null,
      accessReason: null,
      billingMode: null,
      isLoading: true,
      error: null,
    };
  });

  const fetchTenant = async (): Promise<void> => {
    setContext((prev) => ({ ...prev, error: null }));

    try {
      const accessToken = await resolveBearerAccessToken();

      if (!accessToken) {
        throw new Error('No authentication token found');
      }

      // Use authenticated endpoint that gets tenant from token
      const response = await fetch(
        `${environment.apiBaseUrl}/api/public/tenant/subscription-status`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const rawData: unknown = await response.json();

      // Validate response with Zod schema from shared package
      const validatedData = TenantResponseSchema.parse(rawData);

      if (validatedData.success && validatedData.data) {
        const tenantData = validatedData.data;

        // Determine error message based on status
        let errorMessage: string | null = null;
        if (!tenantData.isActive) {
          if (!tenantData.hasSubscription) {
            errorMessage = 'No active subscription found. Please contact your administrator to set up a subscription.';
          } else if (tenantData.accessReason) {
            errorMessage = tenantData.accessReason;
          } else if (tenantData.subscriptionStatus === 'canceled' || tenantData.subscriptionStatus === 'Canceled') {
            errorMessage = 'Your subscription has been canceled. Please contact your administrator to reactivate.';
          } else if (tenantData.subscriptionStatus === 'past_due' || tenantData.subscriptionStatus === 'PastDue') {
            errorMessage = 'Your subscription payment is past due. Please update your payment method to restore access.';
          } else if (tenantData.subscriptionStatus === 'incomplete' || tenantData.subscriptionStatus === 'Incomplete') {
            errorMessage = 'Your subscription setup is incomplete. Please complete the payment setup.';
          } else {
            errorMessage = 'This tenant account is inactive. Please contact support.';
          }
        }

        setContext({
          tenantId: tenantData.tenantId,
          tenantName: tenantData.tenantName,
          subdomain: tenantData.subdomain,
          isActive: tenantData.isActive,
          hasSubscription: tenantData.hasSubscription || false,
          subscriptionStatus: tenantData.subscriptionStatus || null,
          accessReason: tenantData.accessReason ?? null,
          billingMode: tenantData.billingMode ?? null,
          isLoading: false,
          error: errorMessage
        });
      } else {
        setContext({
          tenantId: null,
          tenantName: null,
          subdomain: null,
          isActive: false,
          hasSubscription: false,
          subscriptionStatus: null,
          accessReason: null,
          billingMode: null,
          isLoading: false,
          error: 'Tenant not found'
        });
      }
    } catch (error) {
      console.error('Failed to fetch tenant:', error);

      let errorMessage = 'Failed to load tenant';

      if (error instanceof z.ZodError) {
        errorMessage = 'Invalid tenant data received from server';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setContext({
        tenantId: null,
        tenantName: null,
        subdomain: null,
        isActive: false,
        hasSubscription: false,
        subscriptionStatus: null,
        accessReason: null,
        billingMode: null,
        isLoading: false,
        error: errorMessage
      });
    }
  };

  const refreshTenant = async (): Promise<void> => {
    await fetchTenant();
  };

  const clearTenant = (): void => {
    setContext({
      tenantId: null,
      tenantName: null,
      subdomain: null,
      isActive: false,
      hasSubscription: false,
      subscriptionStatus: null,
      accessReason: null,
      billingMode: null,
      isLoading: false,
      error: null
    });
  };
  const { pathname } = useLocation();
  const tenantSlug = extractTenantSlug(pathname);

  useEffect(() => {
    // Provider users don't need subscription checks
    if (dt.isProviderRole(userRole)) {
      setContext({
        tenantId: null,
        tenantName: null,
        subdomain: null,
        isActive: true,
        hasSubscription: false,
        subscriptionStatus: null,
        accessReason: null,
        billingMode: null,
        isLoading: false,
        error: null
      });
      return;
    }

    // Only show loading on first load or when the tenant slug in the URL changes.
    setContext((prev) => ({
      ...prev,
      isLoading: prev.tenantId == null || (tenantSlug != null && prev.subdomain !== tenantSlug),
    }));

    // Check if user is authenticated via Amplify
    const checkAuthAndFetchTenant = async () => {
      try {
        const accessToken = await resolveBearerAccessToken();

        if (accessToken) {
          // Always check tenant subscription status if authenticated
          await fetchTenant();
        } else {
          // No auth token - set inactive state
          setContext((prev) => ({
            ...prev,
            tenantId: null,
            tenantName: null,
            subdomain: extractTenantSlug(pathname),
            isActive: false,
            isLoading: false,
            error: 'Not authenticated'
          }));
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setContext((prev) => ({
          ...prev,
          isActive: false,
          isLoading: false,
          error: 'Authentication check failed'
        }));
      }
    };

    checkAuthAndFetchTenant();
  }, [tenantSlug, userRole, pathname]);

  const value: SubdomainContextValue = {
    ...context,
    refreshTenant,
    clearTenant
  };

  return (
    <SubdomainContext.Provider value={value}>
      {children}
    </SubdomainContext.Provider>
  );
}

/**
 * Hook to access tenant context (slug from URL path).
 */
export function useSubdomain(): SubdomainContextValue {
  const context = useContext(SubdomainContext);

  if (context === undefined) {
    throw new Error('useSubdomain must be used within a SubdomainProvider');
  }

  return context;
}

/**
 * Hook for navigation within the current tenant path prefix.
 */
export function useSubdomainNavigate(): (path: string, options?: Record<string, unknown>) => void {
  const navigate = useNavigate();

  return (path: string, options?: Record<string, unknown>): void => {
    navigate(path, options);
  };
}

/**
 * Get current tenant slug from the URL path.
 */
export function getCurrentSubdomain(): string | null {
  return extractTenantSlug(window.location.pathname);
}

/**
 * Returns the path unchanged — tenant slug is in the URL path prefix.
 */
export function useSubdomainPath(path: string): string {
  return path;
}
